import SalviaLayout from "@/components/SalviaLayout";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useAuth } from "@/_core/hooks/useAuth";
import { useLocation } from "wouter";
import { AlertCircle, Lock, ArrowRight, Zap } from "lucide-react";
import { usePageTitle } from "@/hooks/usePageTitle";

export default function MotorAnalise() {
  usePageTitle("Motor de Análise");
  const { user } = useAuth();
  const [, setLocation] = useLocation();

  const isInternalAdmin = user?.role === "admin" || user?.role === "salvia_operation_admin";

  // Proteção: administradores internos Sálvia possuem o mesmo acesso operacional no NR1
  if (!isInternalAdmin) {
    return (
      <SalviaLayout title="Motor de Análise">
        <div className="flex items-center justify-center h-full">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertCircle className="text-red-500" size={24} />
                Acesso Não Autorizado
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground">
                O Módulo de Motor de Análise é exclusivo para administradores internos da Sálvia.
              </p>
              <Button 
                onClick={() => setLocation("/dashboard")}
                className="mt-4 w-full"
              >
                Voltar ao Dashboard
              </Button>
            </CardContent>
          </Card>
        </div>
      </SalviaLayout>
    );
  }

  return (
    <SalviaLayout title="Motor de Análise">
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold">Motor de Análise</h1>
          <p className="text-gray-600 mt-2">Selecione uma metodologia para gerenciar templates, matrizes e versionamento</p>
        </div>

        {/* Grid de Metodologias */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Card ERGOS */}
          <Card className="border-2 border-blue-200 hover:border-blue-400 transition cursor-pointer" onClick={() => setLocation("/motor-analise/ergos")}>
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-blue-100 rounded-lg">
                    <Zap className="w-6 h-6 text-blue-600" />
                  </div>
                  <div>
                    <CardTitle className="text-xl">ERGOS</CardTitle>
                    <CardDescription>Metodologia ativa</CardDescription>
                  </div>
                </div>
                <Badge className="bg-green-600 text-white">Configurado</Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Gerenciar templates, matrizes de qualificação, fontes geradoras, classificação e sugestões.
              </p>
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-xs text-blue-700 font-medium">Status: Publicado</p>
                <p className="text-xs text-blue-600">Todos os componentes configurados</p>
              </div>
              <Button className="w-full bg-blue-600 hover:bg-blue-700" onClick={() => setLocation("/motor-analise/ergos")}>
                Entrar no ERGOS
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </CardContent>
          </Card>

          {/* Card COPSOQ */}
          <Card className="border-2 border-gray-200 opacity-60">
            <CardHeader>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-gray-100 rounded-lg">
                    <Lock className="w-6 h-6 text-gray-600" />
                  </div>
                  <div>
                    <CardTitle className="text-xl">COPSOQ</CardTitle>
                    <CardDescription>Metodologia</CardDescription>
                  </div>
                </div>
                <Badge variant="secondary">Em breve</Badge>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-sm text-muted-foreground">
                Metodologia complementar para análise de fatores psicossociais. Disponível em breve.
              </p>
              <Alert>
                <Lock className="h-4 w-4" />
                <AlertDescription>
                  Esta metodologia estará disponível em uma próxima versão da plataforma.
                </AlertDescription>
              </Alert>
              <Button className="w-full" disabled>
                Em breve
              </Button>
            </CardContent>
          </Card>
        </div>

        {/* Info Card */}
        <Card className="bg-blue-50 border-blue-200">
          <CardHeader>
            <CardTitle className="text-base flex items-center gap-2">
              <AlertCircle className="w-5 h-5 text-blue-600" />
              Dica de Navegação
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-sm text-blue-900">
              Clique em "Entrar no ERGOS" para acessar o painel completo com templates, matrizes, wizard de configuração e relatórios de auditoria.
            </p>
          </CardContent>
        </Card>
      </div>
    </SalviaLayout>
  );
}
